import { useEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import type { SignDocumentDetail } from '@todo/shared';
import { ApiError } from '../api/client';
import { signApi } from '../api/sign.api';
import { PAGE_BASE_WIDTH, renderPdfPages } from '../lib/pdf-render';
import { ZoomControl } from './ZoomControl';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

/**
 * The sender's live progress view: the document's pages with every placed
 * box in its CURRENT state - a signed box shows the actual signature (green
 * edge), a pending one a dashed grey "Awaiting <name>". Read-only.
 */
export function SignProgressViewer({
  document: doc,
  onClose,
}: {
  document: SignDocumentDetail;
  onClose: () => void;
}) {
  const [pageCount, setPageCount] = useState(0);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const pageWidth = Math.round(PAGE_BASE_WIDTH * zoom);
  const pdfRef = useRef<pdfjs.PDFDocumentProxy | null>(null);
  const canvasRefs = useRef(new Map<number, HTMLCanvasElement>());

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const bytes = await signApi.fileBytes(doc.id);
        const pdf = await pdfjs.getDocument({ data: bytes }).promise;
        if (cancelled) return;
        pdfRef.current = pdf;
        setPageCount(pdf.numPages);
      } catch {
        setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [doc.id]);

  useEffect(() => {
    const pdf = pdfRef.current;
    if (!pdf || pageCount === 0) return;
    const job = renderPdfPages(pdf, (n) => canvasRefs.current.get(n), pageWidth);
    return () => job.cancel();
  }, [pageCount, pageWidth]);

  /** The progress as a PDF, built by the API (signature images are not readable from the page). */
  async function download() {
    setDownloading(true);
    setDownloadError(null);
    try {
      const { bytes, fileName } = await signApi.progressPdf(doc.id);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setDownloadError(err instanceof ApiError ? err.message : 'The progress PDF could not be built.');
    } finally {
      setDownloading(false);
    }
  }

  /** Only SIGNATURE boxes count towards "signed" - a comment box is not
   *  a signature, and counting it made the tally overstate progress. */
  const signatureBoxes = doc.overlays.filter((o) => o.kind === 'SIGNATURE');
  const signedCount = signatureBoxes.filter((o) => o.signatureUrl).length;
  const commentCount = doc.overlays.filter(
    (o) => o.kind === 'COMMENT' && o.valueText,
  ).length;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-100">
      <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{doc.title}</p>
          <p className="text-xs text-slate-500">
            Signing progress · {signedCount}/{signatureBoxes.length} signed
            {commentCount > 0 && ` · ${commentCount} comment${commentCount === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {downloadError && <span className="text-xs text-red-600">{downloadError}</span>}
          <ZoomControl zoom={zoom} onChange={setZoom} />
          <button
            type="button"
            disabled={downloading || failed}
            onClick={() => void download()}
            className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50"
          >
            {downloading ? 'Preparing…' : 'Download PDF'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto py-6">
        {failed && (
          <p className="py-16 text-center text-sm text-red-600">Could not load the PDF.</p>
        )}
        {pageCount === 0 && !failed && (
          <p className="py-16 text-center text-sm text-slate-500">Loading PDF…</p>
        )}
        <div className="mx-auto space-y-6" style={{ width: pageWidth }}>
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
            <div key={n} className="relative rounded border border-slate-300 bg-white shadow-sm">
              <canvas ref={(el) => { if (el) canvasRefs.current.set(n, el); }} className="block" />
              {doc.overlays
                .filter((o) => o.page === n)
                .map((o, i) =>
                  /* A comment box renders its TEXT, never a signature
                     image - and it renders even when the person has not
                     signed yet, because a comment can be typed and the
                     signature declined. */
                  o.kind === 'COMMENT' ? (
                    <div
                      key={i}
                      style={{
                        left: `${o.x * 100}%`,
                        top: `${o.y * 100}%`,
                        width: `${o.width * 100}%`,
                        height: `${o.height * 100}%`,
                      }}
                      title={o.valueText
                        ? `Comment from ${o.recipientName}: ${o.valueText}`
                        : `Comment box for ${o.recipientName} - nothing typed`}
                      className={[
                        'pointer-events-none absolute overflow-hidden rounded border-2 px-1 py-0.5',
                        o.valueText
                          ? 'border-sky-500 bg-sky-50'
                          : 'border-dashed border-slate-400 bg-slate-100/60',
                      ].join(' ')}
                    >
                      {o.valueText ? (
                        <p className="h-full overflow-hidden text-[10px] leading-snug text-slate-800">
                          {o.valueText}
                        </p>
                      ) : (
                        <span className="flex h-full items-center justify-center text-[10px] font-semibold text-slate-500">
                          No comment
                        </span>
                      )}
                    </div>
                  ) : o.signatureUrl ? (
                    <div
                      key={i}
                      style={{
                        left: `${o.x * 100}%`,
                        top: `${o.y * 100}%`,
                        width: `${o.width * 100}%`,
                        height: `${o.height * 100}%`,
                      }}
                      className="pointer-events-none absolute rounded border border-emerald-400"
                      title={`Signed by ${o.recipientName}`}
                    >
                      <img
                        src={o.signatureUrl}
                        alt={`Signature of ${o.recipientName}`}
                        className="h-full w-full object-contain"
                      />
                    </div>
                  ) : (
                    <div
                      key={i}
                      style={{
                        left: `${o.x * 100}%`,
                        top: `${o.y * 100}%`,
                        width: `${o.width * 100}%`,
                        height: `${o.height * 100}%`,
                      }}
                      className="pointer-events-none absolute flex items-center justify-center rounded border-2 border-dashed border-slate-400 bg-slate-200/50"
                    >
                      <span className="truncate rounded bg-slate-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                        Awaiting {o.recipientName}
                      </span>
                    </div>
                  ),
                )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
